import { AxiosError } from 'axios';

import {
  FieldIssue,
  PROVIDER_AUTH_ERROR_STATUS,
  UPDATE_APP_VERSION_STATUS,
  UnsupportedAppVersionResult,
  ValidationResult
} from './apiTypes.shim';

export abstract class BaseHttpError extends Error {
  // Provide the full http client error so no information about the exception is held
  // back from the UI component that needs to handle the exception.
  //
  // Yeah this binds us to Axios because it uses a concrete error class from Axios.
  // Known tradeoff for now for speed to market because we do not forsee changing
  // the HTTP client. (Famous last words.)
  axiosError: AxiosError;

  constructor(axiosError: AxiosError) {
    super(axiosError.message);
    Object.setPrototypeOf(this, BaseHttpError.prototype);
    this.axiosError = axiosError;
  }

  getContext() {
    return {
      requestUrl: this.axiosError?.config?.url,
      requestBody: this.axiosError?.config?.data,
      requestMethod: this.axiosError?.config?.method,
      responseStatus: this.axiosError?.response?.status,
      responseStatusText: this.axiosError?.response?.statusText,
      responseBody: this.axiosError?.response?.data,
      errorMessage: this.axiosError?.message,
      originalError: this.axiosError
    };
  }
}

/**
 * An HTTP Error without a response or one with a status we did not expect
 */
export class HttpError extends BaseHttpError {
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}

/**
 * An HTTP Error with a status code that matches a non-numeric Axios network error
 */
export class HttpConnectionError extends BaseHttpError {
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, HttpConnectionError.prototype);
  }
}

/**
 * HTTP 4xx Response Code Errors
 */
export class HttpClientError extends BaseHttpError {
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, HttpClientError.prototype);
  }
}

export class BadRequestError extends HttpClientError {
  /** response body data for response returning with status code === 400 */
  responseData: ValidationResult;

  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, BadRequestError.prototype);
    const axiosErrorResponse = axiosError.response?.data as Partial<ValidationResult>;

    this.responseData = {
      ...axiosErrorResponse,
      nonFieldErrors: axiosErrorResponse?.nonFieldErrors || [],
      conflictErrors: axiosErrorResponse?.conflictErrors || [],
      fieldIssues: axiosErrorResponse?.fieldIssues || []
    };
  }
}

export class UnauthorizedError extends HttpClientError {
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, UnauthorizedError.prototype);
  }
}

export class ForbiddenError extends HttpClientError {
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, ForbiddenError.prototype);
  }
}

export class NotFoundError extends HttpClientError {
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

export class ProviderAuthError extends HttpClientError {
  responseData: ValidationResult;
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, ProviderAuthError.prototype);
    const axiosErrorResponse = axiosError.response?.data as Partial<ValidationResult>;

    this.responseData = {
      ...axiosErrorResponse,
      nonFieldErrors: axiosErrorResponse?.nonFieldErrors || [],
      conflictErrors: axiosErrorResponse?.conflictErrors || [],
      fieldIssues: axiosErrorResponse?.fieldIssues || []
    };
  }
}

export class UnsupportedAppVersionError extends HttpClientError {
  responseData: UnsupportedAppVersionResult;
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, UnsupportedAppVersionError.prototype);
    const axiosErrorResponse = axiosError.response?.data as Partial<UnsupportedAppVersionResult>;

    this.responseData = {
      ...axiosErrorResponse,
      message: axiosErrorResponse?.message || null,
      appName: axiosErrorResponse?.appName || null,
      appVersion: axiosErrorResponse?.appVersion || null,
      minimumVersions: axiosErrorResponse?.minimumVersions || {
        callPilot: null,
        portal: null
      }
    };
  }
}

/**
 * HTTP 5xx Response Code Errors
 */
export class HttpServerError extends BaseHttpError {
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, HttpServerError.prototype);
  }
}

class InternalServerError extends HttpServerError {
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, InternalServerError.prototype);
  }
}

class ServiceUnavailableError extends HttpServerError {
  constructor(axiosError: AxiosError) {
    super(axiosError);
    Object.setPrototypeOf(this, ServiceUnavailableError.prototype);
  }
}

/**
 * Helper function that we can re-use and combine 400 error messages as a simple string array
 *
 * @returns string array that contains all non-field and field errors
 */
export const combineErrorsAndIssues = (
  nonFieldErrors: string[] | undefined,
  fieldIssues: FieldIssue[] | undefined
): string[] => {
  const displayErrors: string[] = nonFieldErrors ? [...nonFieldErrors] : [];

  if (fieldIssues?.length)
    fieldIssues.forEach(({ path, message }) => displayErrors.push(`${path.join('.')}: ${message}`));

  return displayErrors;
};

export function getErrorsForPath(fieldIssues: FieldIssue[], path: FieldIssue['path']): string[] {
  return fieldIssues
    .filter((issue) => issue.path.every((pathValue, index) => pathValue === path[index]))
    .map((issue) => issue.message);
}

export function axiosErrorToHttpError(err: AxiosError): BaseHttpError {
  const AXIOS_NETWORK_ERROR_CODES = [AxiosError.ERR_NETWORK, AxiosError.ECONNABORTED, AxiosError.ETIMEDOUT];
  if (!err.response?.status && err.code && AXIOS_NETWORK_ERROR_CODES.includes(err.code)) {
    return new HttpConnectionError(err);
  }
  if (!err.response?.status) return new HttpError(err);

  switch (err.response.status) {
    case 400:
      return new BadRequestError(err);
    case 401:
      return new UnauthorizedError(err);
    case 403:
      return new ForbiddenError(err);
    case 404:
      return new NotFoundError(err);
    case UPDATE_APP_VERSION_STATUS:
      return new UnsupportedAppVersionError(err);
    case PROVIDER_AUTH_ERROR_STATUS: // Integration Request Authentication Error
      return new ProviderAuthError(err);
    case 500:
      return new InternalServerError(err);
    case 503:
      return new ServiceUnavailableError(err);
    default:
      return err.response.status >= 500 ? new HttpServerError(err) : new HttpError(err);
  }
}
